import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import i18n from "i18";
import _request from "actions/_request";

const TOPIC_KEYS = [
  "support.topics.bug",
  "support.topics.billing",
  "support.topics.account",
  "support.topics.feature",
  "support.topics.other",
];

export default class SupportButton extends React.Component {
  state = {
    open: false,
    step: 0,
    topic: "",
    customTopic: "",
    message: "",
    screenshots: [],
    loading: false,
    success: false,
    error: "",
  };

  openDialog = () =>
    this.setState({
      open: true,
      step: 0,
      topic: "",
      customTopic: "",
      message: "",
      screenshots: [],
      loading: false,
      success: false,
      error: "",
    });

  closeDialog = () => this.setState({ open: false });

  handleTopicSelect = (topic) => this.setState({ topic });

  goNext = () => this.setState((s) => ({ step: s.step + 1 }));
  goBack = () => this.setState((s) => ({ step: s.step - 1 }));

  handleScreenshotUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const { screenshots } = this.state;
    const remaining = 10 - screenshots.length;
    const newFiles = files.slice(0, remaining);
    const withPreviews = newFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    this.setState({ screenshots: [...screenshots, ...withPreviews] });
    e.target.value = "";
  };

  removeScreenshot = (idx) => {
    const screenshots = [...this.state.screenshots];
    const removed = screenshots.splice(idx, 1);
    if (removed[0]?.preview) URL.revokeObjectURL(removed[0].preview);
    this.setState({ screenshots });
  };

  handleSubmit = async () => {
    const { topic, customTopic, message, screenshots } = this.state;
    const finalTopic =
      topic === "support.topics.other" ? customTopic : i18n.t(topic);

    this.setState({ loading: true, error: "" });

    const formData = new FormData();
    formData.append("subject", finalTopic);
    formData.append("message", message);
    screenshots.forEach(({ file }) =>
      formData.append("screenshots[]", file)
    );

    try {
      const resp = await _request.support.send(formData);
      if (resp?.isOk) {
        this.setState({ loading: false, success: true });
      } else {
        this.setState({ loading: false, error: i18n.t("support.error") });
      }
    } catch {
      this.setState({ loading: false, error: i18n.t("support.error") });
    }
  };

  /* ─── Step 0: Topic ─────────────────────────────────────────────────────── */
  renderStepTopic() {
    const { topic, customTopic } = this.state;
    return (
      <Box className="flex column gap16">
        <Typography
          sx={{ color: "var(--text-color)", fontSize: 14, fontWeight: 400 }}
        >
          {i18n.t("support.topicHint")}
        </Typography>
        <Box className="flex gap8" sx={{ flexWrap: "wrap" }}>
          {TOPIC_KEYS.map((key) => {
            const active = topic === key;
            return (
              <Button
                key={key}
                size="small"
                variant={active ? "contained" : "outlined"}
                onClick={() => this.handleTopicSelect(key)}
                sx={{
                  borderRadius: 6,
                  textTransform: "none",
                  fontSize: 13,
                  ...(active
                    ? { backgroundColor: "#42C0C7", borderColor: "#42C0C7" }
                    : {
                        borderColor: "var(--border-color)",
                        color: "var(--text-color)",
                      }),
                }}
              >
                {i18n.t(key)}
              </Button>
            );
          })}
        </Box>
        {topic === "support.topics.other" && (
          <TextField
            variant="outlined"
            size="small"
            value={customTopic}
            onChange={(e) => this.setState({ customTopic: e.target.value })}
            placeholder={i18n.t("support.customTopicPlaceholder")}
            sx={textFieldSx}
            fullWidth
          />
        )}
      </Box>
    );
  }

  /* ─── Step 1: Message ────────────────────────────────────────────────────── */
  renderStepMessage() {
    const { message } = this.state;
    return (
      <Box className="flex column gap16">
        <Typography
          sx={{ color: "var(--text-color)", fontSize: 14, fontWeight: 400 }}
        >
          {i18n.t("support.messageHint")}
        </Typography>
        <TextField
          multiline
          rows={5}
          variant="outlined"
          value={message}
          onChange={(e) => this.setState({ message: e.target.value })}
          placeholder={i18n.t("support.messagePlaceholder")}
          sx={textFieldSx}
          fullWidth
        />
      </Box>
    );
  }

  /* ─── Step 2: Screenshots ────────────────────────────────────────────────── */
  renderStepScreenshots() {
    const { screenshots, loading, error } = this.state;
    const canUpload = screenshots.length < 10;

    return (
      <Box className="flex column gap16">
        <Typography
          sx={{ color: "var(--text-color)", fontSize: 14, fontWeight: 400 }}
        >
          {i18n.t("support.screenshotsHint")}
        </Typography>

        <Box className="flex gap12" sx={{ flexWrap: "wrap" }}>
          {screenshots.map(({ preview }, idx) => (
            <Box
              key={idx}
              sx={{
                position: "relative",
                width: 90,
                height: 90,
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid var(--border-color)",
              }}
            >
              <img
                src={preview}
                alt={`screenshot-${idx}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <IconButton
                size="small"
                onClick={() => this.removeScreenshot(idx)}
                sx={{
                  position: "absolute",
                  top: 2,
                  right: 2,
                  backgroundColor: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  padding: "2px",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
                }}
              >
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}

          {canUpload && (
            <Box
              component="label"
              htmlFor="support-file-input"
              sx={{
                width: 90,
                height: 90,
                borderRadius: 2,
                border: "2px dashed var(--border-color, #ccc)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "var(--text-color)",
                fontSize: 28,
                fontWeight: 300,
                userSelect: "none",
                "&:hover": { borderColor: "#42C0C7", color: "#42C0C7" },
              }}
            >
              +
              <input
                id="support-file-input"
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={this.handleScreenshotUpload}
              />
            </Box>
          )}
        </Box>

        <Typography
          sx={{ color: "var(--text-secondary, #888)", fontSize: 12 }}
        >
          {i18n.t("support.screenshotsCount", {
            current: screenshots.length,
            max: 10,
          })}
        </Typography>

        {error && (
          <Typography sx={{ color: "#FE8484", fontSize: 13 }}>
            {error}
          </Typography>
        )}

        <Box className="flex end gap12" sx={{ marginTop: 1 }}>
          <Button
            variant="outlined"
            onClick={this.goBack}
            disabled={loading}
            sx={{ textTransform: "none", borderColor: "var(--border-color)", color: "var(--text-color)" }}
          >
            {i18n.t("cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={this.handleSubmit}
            disabled={loading}
            sx={{ textTransform: "none", backgroundColor: "#42C0C7" }}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loading ? i18n.t("support.sending") : i18n.t("support.send")}
          </Button>
        </Box>
      </Box>
    );
  }

  /* ─── Success screen ─────────────────────────────────────────────────────── */
  renderSuccess() {
    return (
      <Box className="flex column centerH gap16" sx={{ padding: "24px 0", alignItems: "center" }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 56, color: "#42C0C7" }} />
        <Typography sx={{ color: "var(--text-color)", fontSize: 16, fontWeight: 600, textAlign: "center" }}>
          {i18n.t("support.successTitle")}
        </Typography>
        <Typography sx={{ color: "var(--text-secondary, #888)", fontSize: 14, textAlign: "center" }}>
          {i18n.t("support.successDesc")}
        </Typography>
        <Button
          variant="contained"
          onClick={this.closeDialog}
          sx={{ textTransform: "none", backgroundColor: "#42C0C7", marginTop: 1 }}
        >
          {i18n.t("support.close")}
        </Button>
      </Box>
    );
  }

  render() {
    const { open, step, topic, customTopic, message, success } = this.state;

    const steps = [
      i18n.t("support.stepTopic"),
      i18n.t("support.stepMessage"),
      i18n.t("support.stepScreenshots"),
    ];

    const canGoNextStep0 =
      !!topic && (topic !== "support.topics.other" || customTopic.trim() !== "");
    const canGoNextStep1 = message.trim().length > 0;

    return (
      <>
        <Tooltip title={i18n.t("support.buttonTooltip")} placement="left">
          <Fab
            onClick={this.openDialog}
            size="medium"
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              zIndex: 1300,
              backgroundColor: "#42C0C7",
              color: "#fff",
              "&:hover": { backgroundColor: "#37adb4" },
            }}
          >
            <HeadsetMicIcon />
          </Fab>
        </Tooltip>

        <Dialog
          open={open}
          onClose={this.closeDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: "var(--background-menu)",
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 1,
            }}
          >
            <Typography
              sx={{ color: "var(--text-color)", fontSize: 18, fontWeight: 600 }}
            >
              {i18n.t("support.title")}
            </Typography>
            <IconButton onClick={this.closeDialog} size="small" sx={{ color: "var(--text-color)" }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ paddingTop: "12px !important" }}>
            {success ? (
              this.renderSuccess()
            ) : (
              <Box className="flex column gap24">
                <Stepper activeStep={step} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel
                        sx={{
                          "& .MuiStepLabel-label": { color: "var(--text-color)", fontSize: 12 },
                          "& .MuiStepIcon-root.Mui-active": { color: "#42C0C7" },
                          "& .MuiStepIcon-root.Mui-completed": { color: "#42C0C7" },
                        }}
                      >
                        {label}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>

                <Box sx={{ minHeight: 180 }}>
                  {step === 0 && this.renderStepTopic()}
                  {step === 1 && this.renderStepMessage()}
                  {step === 2 && this.renderStepScreenshots()}
                </Box>

                {step < 2 && (
                  <Box className="flex end gap12">
                    {step > 0 && (
                      <Button
                        variant="outlined"
                        onClick={this.goBack}
                        sx={{ textTransform: "none", borderColor: "var(--border-color)", color: "var(--text-color)" }}
                      >
                        {i18n.t("support.back")}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      onClick={this.goNext}
                      disabled={step === 0 ? !canGoNextStep0 : !canGoNextStep1}
                      sx={{ textTransform: "none", backgroundColor: "#42C0C7" }}
                    >
                      {i18n.t("support.next")}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }
}

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    color: "var(--text-color)",
    "& fieldset": { borderColor: "var(--border-color, #ccc)" },
    "&:hover fieldset": { borderColor: "#42C0C7" },
    "&.Mui-focused fieldset": { borderColor: "#42C0C7" },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "var(--text-secondary, #888)",
    opacity: 1,
  },
};
